/**
 * Verification runtime context: first-run detection, workspace-keyed
 * preference, safe URL override, and detail-clearing on switch.
 *
 * Product Environment identity is This Mac (`real`). Demo remains a namespaced
 * fixture scope for isolation tests and legacy bookmarks, not a chooser option.
 *
 * A fresh profile is zero-setup: an absent preference adopts and persists This
 * Mac (`real`) with source=`persisted` and no chooser. An explicit Settings
 * reset is a different state, recorded as a sentinel, and that one does open the
 * chooser — "never chose" and "ask me again" must not collapse into one `null`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_VERIFICATION_ENVIRONMENT,
  type EnvWorkspaceId,
  type VerificationEnvironment,
  VERIFICATION_ENV_QUERY,
  objectIdsFromPathname,
  parseVerificationEnvironment,
  pathWithoutObjectDetail,
  resolveActiveEnvironment,
  shouldClearObjectDetail,
} from "./env";
import {
  loadEnvironmentPreferenceState,
  loadLegacyEnvironmentPreference,
  markEnvironmentPreferenceCleared,
  saveEnvironmentPreference,
} from "./env-storage";
import { EnvChooser } from "./env-chooser";
import { advanceVerificationRequestGeneration } from "./api/canonical-clients";
import {
  CanonicalEnvironmentProvider,
  canonicalLocalEnvironmentAvailable,
  useCanonicalEnvironmentOptional,
  type CanonicalEnvironmentIdentity,
} from "./canonical-environment-provider";
import { hrefWithoutCanonicalEnvironmentSelection } from "./environment-navigation";

export type VerificationEnvContextValue = {
  ready: boolean;
  environment: VerificationEnvironment | null;
  source: "persisted" | "url" | "none";
  needsChooser: boolean;
  choose: (environment: VerificationEnvironment) => void;
  switchEnvironment: (environment: VerificationEnvironment) => void;
  resetEnvironment: () => void;
};

type EnvProviderProps = {
  children: ReactNode;
  pathname?: string;
  search?: string;
  workspaceId?: EnvWorkspaceId;
  canonicalIdentity?: CanonicalEnvironmentIdentity | null;
  cacheNamespace?: string;
  onNavigate?: (to: string) => void;
  /** Navigation path for state resets; it must not re-apply the current target. */
  onNavigateWithoutEnvironment?: (to: string) => void;
};

const VerificationEnvContext = createContext<VerificationEnvContextValue | null>(null);
let environmentProviderInstance = 0;

function CanonicalEnvChooser({
  onChoose,
}: {
  onChoose: (environment: VerificationEnvironment) => void;
}) {
  const canonical = useCanonicalEnvironmentOptional();
  if (!canonical?.response) return null;
  // A reset must not trap a person behind a local-only modal. Remote and
  // Offline targets remain selectable from the shared canonical control, and
  // an ordinary browser must not be asked to accept a fabricated local claim.
  if (!canonicalLocalEnvironmentAvailable(canonical)) return null;
  return <EnvChooser onChoose={onChoose} />;
}

function urlOverrideFromSearch(search: string): string | null {
  try {
    return new URLSearchParams(search).get(VERIFICATION_ENV_QUERY);
  } catch {
    return null;
  }
}

function EnvProviderTree({
  pathname = "/",
  search = "",
  workspaceId,
  canonicalIdentity,
  cacheNamespace,
  onNavigate,
  onNavigateWithoutEnvironment,
  children,
}: EnvProviderProps) {
  const [cacheInstance] = useState(() => ++environmentProviderInstance);
  const [ready, setReady] = useState(false);
  const [persisted, setPersisted] = useState<VerificationEnvironment | null>(null);
  const objectIds = useMemo(() => objectIdsFromPathname(pathname), [pathname]);
  const urlOverride = urlOverrideFromSearch(search);

  useEffect(() => {
    const state = loadEnvironmentPreferenceState(workspaceId);
    const scopedStored = state.kind === "chosen" ? state.environment : null;
    // A cleared sentinel or an unparseable value both mean "ask" — never guess.
    const askAgain = state.kind === "cleared" || state.kind === "invalid";
    const legacyStored = loadLegacyEnvironmentPreference();
    const stored = scopedStored ?? (legacyStored === "demo" ? "real" : legacyStored);
    if (!scopedStored && legacyStored === "demo") {
      saveEnvironmentPreference("real", workspaceId);
    }
    const resolved = resolveActiveEnvironment({
      persisted: stored,
      urlOverride,
      objectIds,
    });
    if (!stored && resolved.source === "url" && resolved.environment) {
      saveEnvironmentPreference(resolved.environment, workspaceId);
      setPersisted(resolved.environment);
    } else if (!stored && !askAgain) {
      // Zero-setup first run: adopt This Mac rather than asking. A fresh profile
      // must reach the product shell with no environment setup, so an absent
      // preference resolves to `real` with source=`persisted`.
      saveEnvironmentPreference(DEFAULT_VERIFICATION_ENVIRONMENT, workspaceId);
      setPersisted(DEFAULT_VERIFICATION_ENVIRONMENT);
    } else {
      setPersisted(stored);
    }
    setReady(true);
    // Re-hydrate when the workspace changes so org switch cannot reuse scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const resolved = useMemo(
    () =>
      resolveActiveEnvironment({
        persisted,
        urlOverride,
        objectIds,
      }),
    [persisted, urlOverride, objectIds],
  );

  const choose = useCallback(
    (environment: VerificationEnvironment) => {
      saveEnvironmentPreference(environment, workspaceId);
      advanceVerificationRequestGeneration(environment);
      setPersisted(environment);
    },
    [workspaceId],
  );

  const switchEnvironment = useCallback(
    (environment: VerificationEnvironment) => {
      const from = resolved.environment ?? persisted;
      saveEnvironmentPreference(environment, workspaceId);
      advanceVerificationRequestGeneration(environment);
      setPersisted(environment);
      if (from && shouldClearObjectDetail({ from, to: environment, pathname })) {
        onNavigate?.(pathWithoutObjectDetail(pathname));
      }
    },
    [onNavigate, pathname, persisted, resolved.environment, workspaceId],
  );

  const resetEnvironment = useCallback(() => {
    markEnvironmentPreferenceCleared(workspaceId);
    advanceVerificationRequestGeneration(null);
    setPersisted(null);
    // Reset both compatibility state and the canonical URL target. Otherwise a
    // Remote/Offline route survives the reset while legacy-backed pages see no
    // selected environment and strand the person behind an unavailable state.
    const resetHref = hrefWithoutCanonicalEnvironmentSelection(`${pathname}${search}`);
    (onNavigateWithoutEnvironment ?? onNavigate)?.(resetHref);
  }, [onNavigate, onNavigateWithoutEnvironment, pathname, search, workspaceId]);

  const needsChooser = ready && resolved.environment === null;

  const value = useMemo<VerificationEnvContextValue>(
    () => ({
      ready,
      environment: resolved.environment,
      source: resolved.source,
      needsChooser,
      choose,
      switchEnvironment,
      resetEnvironment,
    }),
    [
      ready,
      resolved.environment,
      resolved.source,
      needsChooser,
      choose,
      switchEnvironment,
      resetEnvironment,
    ],
  );

  return (
    <VerificationEnvContext.Provider value={value}>
      <CanonicalEnvironmentProvider
        identity={canonicalIdentity}
        pathname={pathname}
        search={search}
        onNavigate={onNavigate}
        cacheNamespace={`${cacheNamespace ?? "unknown"}:mount:${cacheInstance}`}
      >
        {children}
        {needsChooser ? <CanonicalEnvChooser onChoose={choose} /> : null}
      </CanonicalEnvironmentProvider>
    </VerificationEnvContext.Provider>
  );
}

export function EnvProvider(props: EnvProviderProps) {
  const existing = useContext(VerificationEnvContext);
  if (existing) return <>{props.children}</>;
  return <EnvProviderTree {...props} />;
}

export function useVerificationEnv(): VerificationEnvContextValue {
  const ctx = useContext(VerificationEnvContext);
  if (!ctx) {
    throw new Error("useVerificationEnv must be used inside EnvProvider");
  }
  return ctx;
}

export function useVerificationEnvOptional(): VerificationEnvContextValue | null {
  return useContext(VerificationEnvContext);
}

export function parseEnvSearchParam(search: string): VerificationEnvironment | null {
  return parseVerificationEnvironment(urlOverrideFromSearch(search));
}
